import { useSearchParams } from "react-router";
import { ChoiceCard } from "@omnabase/ui";
import { useSession } from "../../shell/store";
import { useSetParam, useWs, useNavigate } from "../../lib/nav";
import { ADD_SOURCE, includedOf, kb, type AddSourceId, type KnowledgeOverview, type Scope } from "../../lib/knowledge";
import { AddWebsiteDialog } from "./Sources";
import { NEW_ARTICLE } from "./Editor";
import type { KbHref } from "./KnowledgeScreen";

/** A scope with nothing in it: the five ways in. */
export function EmptyKnowledge({ scope, overview, reload, kbHref }: { scope: Scope; overview: KnowledgeOverview; reload: () => void; kbHref: KbHref }) {
  const add = useAddSource(scope, overview, kbHref);
  const [params] = useSearchParams(); const setParam = useSetParam();
  return (
    <section className="ob-listpage" aria-label="Knowledge base">
      <div className="ob-kb-empty">
        <div><h1 className="ob-kb-empty__title">Nothing for the AI to read yet</h1><p className="ob-kb-empty__text">Drafts cite sources from here. Until one exists, the AI answers from the ticket alone and holds more drafts for a person.</p></div>
        <div className="ob-choices">{ADD_SOURCE.map((a) => <ChoiceCard key={a.id} icon={a.icon} title={a.title} body={a.body} badge={a.later ? "Later" : undefined} onClick={() => add(a.id)} />)}</div>
      </div>
      {params.get("add") === "website" && <AddWebsiteDialog scope={scope} onClose={() => setParam("add", null)} onAdded={() => { setParam("add", null); reload(); }} />}
    </section>
  );
}

/** what each way in does: an article opens the editor, which makes the row on the first keystroke (2026-10-16); a website, files or a repository opens its dialog;
 *  resolved tickets makes the project's tickets source (or finds it) and opens its review */
export function useAddSource(scope: Scope, overview: KnowledgeOverview, kbHref: KbHref) {
  const navigate = useNavigate(); const { slug } = useWs(); const { toast } = useSession(); const setParam = useSetParam();
  return (id: AddSourceId) => {
    const way = ADD_SOURCE.find((a) => a.id === id)!;
    if (way.later) { toast(`${way.title} arrives with ${way.later}`); return; }
    if (id === "website" || id === "files" || id === "repository") { setParam("add", id); return; }
    if (id === "tickets") {
      const have = overview.sources.find((s) => s.kind === "tickets");
      if (have) { setParam("review", have.id); return; }
      void kb.addTickets(slug, scope.id).then((s) => { toast(`${includedOf(s)} resolved tickets reused as question-and-answer pairs · names and emails redacted`, { kind: "success" }); setParam("review", s.id); }, (e: Error) => toast(e.message, { kind: "error" }));
      return;
    }
    navigate(kbHref(`/knowledge/articles/${NEW_ARTICLE}`)); // the founder, 2026-10-16: no row until the first keystroke in the editor
  };
}
